import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../config/supabase';

const { FiSearch, FiFilter, FiDownload, FiEye, FiTrash2, FiCalendar, FiUsers, FiTrendingUp, FiMail, FiUser, FiLogOut, FiX } = FiIcons;

function AdminDashboard() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [gameplans, setGameplans] = useState([]);
  const [filteredGameplans, setFilteredGameplans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedGameplan, setSelectedGameplan] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Fetch gameplans from database
  useEffect(() => {
    fetchGameplans();
  }, []);

  useEffect(() => {
    filterGameplans();
  }, [gameplans, searchTerm, statusFilter]);

  const fetchGameplans = async () => {
    try {
      const { data, error } = await supabase
        .from('gameplans')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setGameplans(data || []);
    } catch (error) {
      console.error('Error fetching gameplans:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterGameplans = () => {
    let filtered = gameplans;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(gp => gp.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(gp => 
        gp.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        gp.client_email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredGameplans(filtered);
  };

  const handleViewGameplan = (gameplan) => {
    setSelectedGameplan(gameplan);
    setShowDetails(true);
  };

  const handleDeleteGameplan = async (id) => {
    if (!confirm('Are you sure you want to delete this gameplan?')) return;

    try {
      const { error } = await supabase
        .from('gameplans')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      fetchGameplans(); // Refresh list
    } catch (error) {
      console.error('Error deleting gameplan:', error);
      alert('Error deleting gameplan. Please try again.');
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/admin/login');
  };

  const stats = {
    total: gameplans.length,
    completed: gameplans.filter(gp => gp.status === 'completed').length,
    thisMonth: gameplans.filter(gp => {
      const created = new Date(gp.created_at);
      const now = new Date();
      return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
    }).length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-600">Manage client gameplans</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">Welcome, {user?.email}</span>
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <SafeIcon icon={FiLogOut} />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Gameplans</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <SafeIcon icon={FiUsers} className="text-3xl text-blue-600" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-3xl font-bold text-gray-900">{stats.completed}</p>
              </div>
              <SafeIcon icon={FiTrendingUp} className="text-3xl text-green-600" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">This Month</p>
                <p className="text-3xl font-bold text-gray-900">{stats.thisMonth}</p>
              </div>
              <SafeIcon icon={FiCalendar} className="text-3xl text-purple-600" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiFilter} className="text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </div>

        {/* Gameplans List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {filteredGameplans.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <SafeIcon icon={FiUser} className="text-4xl mx-auto mb-4 text-gray-300" />
              <p>No gameplans found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredGameplans.map((gameplan) => (
                    <tr key={gameplan.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {gameplan.client_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {gameplan.client_email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          gameplan.status === 'completed' ? 'bg-green-100 text-green-800' :
                          gameplan.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {gameplan.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(gameplan.created_at), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewGameplan(gameplan)}
                            className="text-blue-600 hover:text-blue-800"
                            title="View details"
                          >
                            <SafeIcon icon={FiEye} className="text-lg" />
                          </button>
                          {gameplan.status === 'completed' && (
                            <button
                              onClick={() => window.open(`/questionnaire?id=${gameplan.id}`, '_blank')}
                              className="text-green-600 hover:text-green-800"
                              title="View gameplan"
                            >
                              <SafeIcon icon={FiDownload} className="text-lg" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteGameplan(gameplan.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <SafeIcon icon={FiTrash2} className="text-lg" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Gameplan Details Modal */}
        {showDetails && selectedGameplan && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl max-h-[90vh] overflow-y-auto w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    Gameplan Details
                  </h2>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <SafeIcon icon={FiX} className="text-xl" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Client Information</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Name: {selectedGameplan.client_name}</p>
                      <p className="text-sm text-gray-600">Email: {selectedGameplan.client_email}</p>
                      <p className="text-sm text-gray-600">Status: {selectedGameplan.status}</p>
                      <p className="text-sm text-gray-600">Created: {format(new Date(selectedGameplan.created_at), 'PPP')}</p>
                    </div>
                  </div>

                  {selectedGameplan.gameplan_data && (
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-2">Gameplan Summary</h3>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Target Market:</strong> {selectedGameplan.gameplan_data.fiveOnes?.targetMarket}
                        </p>
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Offer:</strong> {selectedGameplan.gameplan_data.fiveOnes?.offer}
                        </p>
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Monthly Goal:</strong> ${selectedGameplan.gameplan_data.revenue?.monthlyGoal?.toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Status:</strong> {selectedGameplan.status}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 pt-4">
                    {selectedGameplan.status === 'completed' && (
                      <button
                        onClick={() => window.open(`/questionnaire?id=${selectedGameplan.id}`, '_blank')}
                        className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        View Full Gameplan
                      </button>
                    )}
                    <button
                      onClick={() => setShowDetails(false)}
                      className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;